import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  updateProfile,
  updateOrgName,
  uploadAvatar,
  deleteAvatar,
  fetchUsageStats,
  openBillingPortal,
  exportPersonalData,
  deleteAccount,
} from '../services/api';
import { JOB_OPTIONS, INDUSTRY_OPTIONS, PRICING_TIERS } from '../../shared/types';
import ApiKeysSection from './ApiKeysSection';
import MfaEnrollModal from './MfaEnrollModal';
import Avatar from './ui/Avatar';
import { disableMfa } from '../services/api';
import { formatDate } from '../utils/format';
import UsageTabContent, { type UsageStats } from './account/UsageTabContent';
import BillingTabContent from './account/BillingTabContent';
import PrivacyTabContent from './account/PrivacyTabContent';
import DeleteAccountModal from './account/DeleteAccountModal';

type SettingsTab = 'general' | 'account' | 'billing' | 'usage' | 'api' | 'privacy';

const DELETE_CONFIRMATION_PHRASE = 'SUPPRIMER MON COMPTE';

interface AccountSettingsPageProps {
  onClose: () => void;
  onUpgrade: () => void;
}

/** Options d'une liste fermée, plus la valeur enregistrée si elle n'en fait
 *  pas partie (ancienne saisie libre) : elle reste affichée et conservée tant
 *  que l'utilisateur n'en choisit pas une autre. */
function optionsWithLegacy(options: readonly string[], saved: string | null | undefined): string[] {
  const legacy = saved?.trim();
  return legacy && !options.includes(legacy) ? [...options, legacy] : [...options];
}

const ORG_ROLE_LABELS: Record<string, string> = {
  owner: 'Propriétaire',
  admin: 'Administrateur',
  member: 'Membre',
};

export default function AccountSettingsPage({ onClose, onUpgrade }: AccountSettingsPageProps) {
  const { user, organization, features, signOut, refreshProfile } = useAuth();
  const [tab, setTab] = useState<SettingsTab>('general');

  // General tab state
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [jobTitle, setJobTitle] = useState(user?.jobTitle ?? '');
  const [industry, setIndustry] = useState(user?.industry ?? '');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Avatar state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Org name editing state
  const [orgName, setOrgName] = useState(organization?.name ?? '');
  const [orgNameEditing, setOrgNameEditing] = useState(false);
  const [orgNameSaving, setOrgNameSaving] = useState(false);

  // Usage tab state
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Billing state
  const [billingLoading, setBillingLoading] = useState(false);

  // MFA (2FA) state
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaUnenrollLoading, setMfaUnenrollLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  // Privacy tab state (RGPD — data export + account deletion)
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Sync form fields when user/org data changes
  useEffect(() => {
    setDisplayName(user?.displayName ?? '');
    setJobTitle(user?.jobTitle ?? '');
    setIndustry(user?.industry ?? '');
  }, [user]);
  useEffect(() => {
    setOrgName(organization?.name ?? '');
  }, [organization]);

  // Load usage stats when switching to usage tab
  useEffect(() => {
    if (tab === 'usage' && !usageStats && !usageLoading) {
      setUsageLoading(true);
      fetchUsageStats()
        .then(setUsageStats)
        .catch(() => {})
        .finally(() => setUsageLoading(false));
    }
  }, [tab, usageStats, usageLoading]);

  const handleSaveProfile = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await updateProfile({
        displayName: displayName.trim(),
        jobTitle: jobTitle.trim(),
        industry,
      });
      await refreshProfile();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  }, [displayName, jobTitle, industry, refreshProfile]);

  const handleOpenBillingPortal = useCallback(async () => {
    setBillingLoading(true);
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch {
      // If no Stripe customer yet, redirect to pricing
      onUpgrade();
    } finally {
      setBillingLoading(false);
    }
  }, [onUpgrade]);

  const handleAvatarChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    setSaveError(null);
    try {
      await uploadAvatar(file);
      await refreshProfile();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Upload échoué');
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [refreshProfile]);

  const handleAvatarDelete = useCallback(async () => {
    setAvatarUploading(true);
    try {
      await deleteAvatar();
      await refreshProfile();
    } catch {
      setSaveError('Impossible de supprimer l\'avatar');
    } finally {
      setAvatarUploading(false);
    }
  }, [refreshProfile]);

  // Désactivation : un code de l'application d'authentification est exigé,
  // même connecté. Un jeton volé ne doit pas pouvoir retirer la protection
  // sans le téléphone.
  const handleMfaUnenroll = useCallback(async () => {
    if (!confirm("Êtes-vous sûr de vouloir désactiver la double authentification ? Votre compte sera moins protégé.")) {
      return;
    }
    const code = prompt("Pour confirmer, saisissez le code à 6 chiffres de votre application d'authentification :");
    if (!code) return;
    setMfaUnenrollLoading(true);
    setMfaError(null);
    try {
      await disableMfa(code.replace(/\s+/g, ''));
      await refreshProfile();
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'Désactivation échouée');
    } finally {
      setMfaUnenrollLoading(false);
    }
  }, [refreshProfile]);

  const handleMfaEnrollSuccess = useCallback(async () => {
    setMfaModalOpen(false);
    await refreshProfile();
  }, [refreshProfile]);

  const handleExportData = useCallback(async () => {
    setExportLoading(true);
    setExportError(null);
    setExportSuccess(false);
    try {
      await exportPersonalData();
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 4000);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export échoué');
    } finally {
      setExportLoading(false);
    }
  }, []);

  const handleDeleteAccount = useCallback(async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteAccount(deleteConfirmation);
      // Deletion succeeded — sign out and close (user no longer exists)
      await signOut();
      onClose();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Suppression échouée');
      setDeleteLoading(false);
    }
  }, [deleteConfirmation, signOut, onClose]);

  const handleOrgNameSave = useCallback(async () => {
    const trimmed = orgName.trim();
    if (!trimmed || trimmed === organization?.name) {
      setOrgNameEditing(false);
      return;
    }
    setOrgNameSaving(true);
    try {
      await updateOrgName(trimmed);
      await refreshProfile();
      setOrgNameEditing(false);
    } catch {
      setSaveError('Impossible de modifier le nom de l\'organisation');
    } finally {
      setOrgNameSaving(false);
    }
  }, [orgName, organization?.name, refreshProfile]);

  const currentPlan = organization?.plan ?? user?.plan ?? 'free';
  const planLabel = currentPlan === 'beta'
    ? 'Accès offert'
    : currentPlan === 'partner'
      ? 'Partenaire'
      : (PRICING_TIERS.find(t => t.plan === currentPlan)?.name ?? 'Gratuit');

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'general', label: 'Général' },
    { key: 'account', label: 'Compte' },
    { key: 'billing', label: 'Facturation' },
    { key: 'usage', label: 'Utilisation' },
    // The "API" tab is hidden unless the org has been granted API access
    // by a super-admin (per-org gate, not just plan-based).
    ...(features.apiAccess ? [{ key: 'api' as const, label: 'API' }] : []),
    { key: 'privacy', label: 'Confidentialité' },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl font-normal text-slate-900 dark:text-white shader:text-white">Paramètres</h1>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
        >
          <svg className="w-5 h-5 text-slate-500 dark:text-slate-300 shader:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="flex gap-8">
        {/* Sidebar tabs */}
        <nav className="w-44 shrink-0 space-y-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                tab === t.key
                  ? 'bg-violet-100 dark:bg-violet-900/30 shader:bg-violet-500/20 text-violet-700 dark:text-violet-300 shader:text-white'
                  : 'text-slate-600 dark:text-slate-400 shader:text-white/85 shader:text-white hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* ── Général ── */}
          {tab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white">Profil</h2>

              {/* Avatar + name row */}
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={avatarUploading}
                    className="relative w-16 h-16 rounded-full overflow-hidden shrink-0 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                  >
                    <Avatar
                      avatarUrl={user?.avatarUrl}
                      name={user?.displayName}
                      email={user?.email}
                      className="w-full h-full"
                      textClassName="text-lg"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      {avatarUploading ? (
                        <svg className="w-5 h-5 text-white animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                          <circle cx="12" cy="13" r="4" />
                        </svg>
                      )}
                    </div>
                  </button>
                  {/* Delete avatar button */}
                  {user?.avatarUrl && !avatarUploading && (
                    <button
                      onClick={handleAvatarDelete}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      title="Supprimer l'avatar"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white truncate">
                    {user?.displayName || user?.email}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80">
                    Plan {planLabel}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 shader:text-white/70 mt-0.5">
                    Cliquez sur l'image pour modifier
                  </p>
                </div>
              </div>

              {/* Form fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-violet-700 dark:text-violet-400 shader:text-white mb-1.5">
                    Nom complet
                  </label>
                  <div className="glow-input">
                    <input
                      type="text"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                      placeholder="Votre nom"
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 shader:bg-white/10 border border-slate-200 dark:border-slate-700 shader:border-white/10 rounded-lg outline-none focus:ring-2 focus:ring-violet-500 text-slate-900 dark:text-white shader:text-white placeholder-slate-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-violet-700 dark:text-violet-400 shader:text-white mb-1.5">
                    Métier
                  </label>
                  <div className="glow-input">
                    <select
                      value={jobTitle}
                      onChange={e => setJobTitle(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 shader:bg-white/10 border border-slate-200 dark:border-slate-700 shader:border-white/10 rounded-lg outline-none focus:ring-2 focus:ring-violet-500 text-slate-900 dark:text-white shader:text-white [&>option]:text-violet-700 dark:[&>option]:text-white dark:[&>option]:bg-slate-800"
                    >
                    <option value="" disabled>Choisissez votre métier</option>
                    {optionsWithLegacy(JOB_OPTIONS, user?.jobTitle).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-violet-700 dark:text-violet-400 shader:text-white mb-1.5">
                  Secteur d'activité
                </label>
                <div className="glow-input">
                  <select
                    value={industry}
                    onChange={e => setIndustry(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 shader:bg-white/10 border border-slate-200 dark:border-slate-700 shader:border-white/10 rounded-lg outline-none focus:ring-2 focus:ring-violet-500 text-slate-900 dark:text-white shader:text-white [&>option]:text-violet-700 dark:[&>option]:text-white dark:[&>option]:bg-slate-800"
                  >
                    <option value="" disabled>Choisissez votre secteur</option>
                    {optionsWithLegacy(INDUSTRY_OPTIONS, user?.industry).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Save button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-5 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                {saveSuccess && (
                  <span className="text-sm text-emerald-600 dark:text-emerald-400">Enregistré !</span>
                )}
                {saveError && (
                  <span className="text-sm text-red-500">{saveError}</span>
                )}
              </div>
            </div>
          )}

          {/* ── Compte ── */}
          {tab === 'account' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white">Compte</h2>

              {/* Email */}
              <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Adresse e-mail</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Organization */}
              {organization && (
                <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Organisation</p>
                    {orgNameEditing ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          autoFocus
                          type="text"
                          value={orgName}
                          onChange={e => setOrgName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleOrgNameSave();
                            if (e.key === 'Escape') { setOrgNameEditing(false); setOrgName(organization.name); }
                          }}
                          className="flex-1 px-2 py-1 text-sm bg-white dark:bg-slate-700 shader:bg-white/10 border border-violet-500 rounded-md outline-none text-slate-900 dark:text-white shader:text-white"
                        />
                        <button
                          onClick={handleOrgNameSave}
                          disabled={orgNameSaving}
                          className="px-3 py-1 text-xs font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                          {orgNameSaving ? '...' : 'OK'}
                        </button>
                        <button
                          onClick={() => { setOrgNameEditing(false); setOrgName(organization.name); }}
                          className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">{organization.name}</p>
                        {(user?.orgRole === 'owner' || user?.orgRole === 'admin') && (
                          <button
                            onClick={() => setOrgNameEditing(true)}
                            className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            title="Modifier"
                          >
                            <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">ID d'organisation</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-slate-600 dark:text-slate-300 shader:text-white/90 bg-slate-100 dark:bg-slate-700 shader:bg-white/10 px-2 py-1 rounded">
                        {organization.id}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(organization.id)}
                        className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        title="Copier"
                      >
                        <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Rôle</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">
                      {ORG_ROLE_LABELS[user?.orgRole ?? ''] ?? user?.orgRole}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mb-0.5">Membre depuis</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">
                      {formatDate(user?.createdAt ?? null)}
                    </p>
                  </div>
                </div>
              )}

              {/* ── Sécurité — Double authentification (MFA) ── */}
              <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white flex items-center gap-2">
                      Double authentification (2FA)
                      {user?.mfaEnrolled && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                          Activée
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80 mt-1 leading-relaxed">
                      {user?.mfaEnrolled
                        ? 'Votre compte est protégé par un second facteur. Un code à 6 chiffres vous sera demandé à chaque connexion.'
                        : 'Ajoutez une couche de sécurité supplémentaire avec une application d\'authentification (Google Authenticator, Authy, 1Password, Bitwarden, etc.).'}
                    </p>
                  </div>
                </div>

                {mfaError && (
                  <p className="text-xs text-red-500">{mfaError}</p>
                )}

                {user?.mfaEnrolled ? (
                  <button
                    onClick={handleMfaUnenroll}
                    disabled={mfaUnenrollLoading}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                  >
                    {mfaUnenrollLoading ? 'Désactivation...' : 'Désactiver la 2FA'}
                  </button>
                ) : (
                  <button
                    onClick={() => { setMfaError(null); setMfaModalOpen(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 transition-all"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Activer la double authentification
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={signOut}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 shader:border-white/10 text-slate-700 dark:text-slate-300 shader:text-white hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Se déconnecter
                </button>
              </div>
            </div>
          )}

          {/* ── Facturation ── */}
          {tab === 'billing' && (
            <BillingTabContent
              currentPlan={currentPlan}
              planLabel={planLabel}
              billingLoading={billingLoading}
              onOpenBillingPortal={handleOpenBillingPortal}
            />
          )}

          {/* ── Utilisation ── */}
          {tab === 'usage' && (
            <UsageTabContent
              usageStats={usageStats}
              usageLoading={usageLoading}
              planLabel={planLabel}
            />
          )}

          {/* ── Clés API ── */}
          {tab === 'api' && features.apiAccess && <ApiKeysSection />}

          {/* ── Confidentialité — RGPD ── */}
          {tab === 'privacy' && (
            <PrivacyTabContent
              exportLoading={exportLoading}
              exportError={exportError}
              exportSuccess={exportSuccess}
              onExport={handleExportData}
              onRequestDelete={() => {
                setDeleteModalOpen(true);
                setDeleteConfirmation('');
                setDeleteError(null);
              }}
            />
          )}
        </div>
      </div>

      {/* ── MFA enrol modal ── */}
      <MfaEnrollModal
        visible={mfaModalOpen}
        onClose={() => setMfaModalOpen(false)}
        onSuccess={handleMfaEnrollSuccess}
      />

      {/* ── Delete confirmation modal ── */}
      {deleteModalOpen && (
        <DeleteAccountModal
          confirmation={deleteConfirmation}
          onConfirmationChange={setDeleteConfirmation}
          loading={deleteLoading}
          error={deleteError}
          phrase={DELETE_CONFIRMATION_PHRASE}
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </div>
  );
}
